/**
 * Per-kind editor facts (sources.md): what the address field asks for, the default config the editor
 * starts from, and which config fields stay editable after creation (sources.md "Mutable config" — the
 * engine's IDENTITY_FIELDS are the rest).
 */

import type { SourceFormat, SourceKind } from '@digester/core';

export interface KindInfo {
  /** Label of the single address field; null when the kind has its own fields instead (email). */
  addressLabel: string | null;
  placeholder: string;
  help?: string;
  /** Default config fields for `sources.resolve` (mode, toggles …). */
  config: Record<string, unknown>;
  /** Config fields that can change after creation. */
  mutable: readonly string[];
}

export const KINDS: Readonly<Record<SourceKind, KindInfo>> = {
  rss: { addressLabel: 'Feed or page URL', placeholder: 'https://example.com/feed.xml', help: 'A page address works too when it links to its feed.', config: {}, mutable: [] },
  email: { addressLabel: null, placeholder: '', config: { security: 'tls', port: 993, mailbox: 'INBOX' }, mutable: ['security', 'fromFilter'] },
  youtube: {
    addressLabel: 'Channel',
    placeholder: 'https://www.youtube.com/@channel, @handle or UC… id',
    help: 'Each new video arrives with its transcript when YouTube provides one.',
    config: { languages: [], skipShorts: true },
    mutable: ['languages', 'skipShorts'],
  },
  github: {
    addressLabel: 'Repository',
    placeholder: 'owner/repo or https://github.com/owner/repo',
    help: 'Without a token GitHub allows 60 requests an hour from your address; a token raises that to 5,000.',
    config: { issues: true, comments: true, pullRequests: false },
    mutable: ['issues', 'comments', 'pullRequests'],
  },
  telegram: { addressLabel: 'Public channel', placeholder: '@channel or t.me/channel', help: 'Only channels with a public web preview.', config: { mode: 'public' }, mutable: [] },
  mastodon: {
    addressLabel: 'Account',
    placeholder: '@user@mastodon.social or a profile URL',
    config: { mode: 'account', includeReplies: false, includeBoosts: true },
    mutable: ['includeReplies', 'includeBoosts'],
  },
  bluesky: {
    addressLabel: 'Account, feed or list',
    placeholder: 'name.bsky.social, a DID, or a profile / feed / list URL',
    config: { includeReplies: false, includeReposts: false },
    mutable: ['includeReplies', 'includeReposts'],
  },
  reddit: { addressLabel: 'Subreddit or user', placeholder: 'r/name, u/name or a Reddit URL', config: { listing: 'new' }, mutable: [] },
  discourse: { addressLabel: 'Forum', placeholder: 'https://forum.example.org, optionally /c/slug/id or /tag/name', config: {}, mutable: [] },
  watcher: {
    addressLabel: 'What is watched',
    placeholder: 'A page, profile or list',
    help: 'A watcher is fed by push: from the mobile apps’ feeder, or from any tool that can send a web request.',
    config: { platform: 'webhook' },
    mutable: [],
  },
};

export const PULL_OPTIONS: ReadonlyArray<readonly [number, string]> = [
  [5, 'Every 5 minutes'],
  [10, 'Every 10 minutes'],
  [15, 'Every 15 minutes'],
  [30, 'Every 30 minutes'],
  [60, 'Every hour'],
  [180, 'Every 3 hours'],
  [360, 'Every 6 hours'],
  [1440, 'Daily'],
];

export const DEFAULT_PULL: Readonly<Record<SourceKind, number>> = {
  rss: 15,
  email: 15,
  youtube: 60,
  github: 30,
  telegram: 10,
  mastodon: 15,
  bluesky: 15,
  reddit: 30,
  discourse: 30,
  watcher: 0,
};

export const FORMAT_OPTIONS: ReadonlyArray<readonly [SourceFormat, string]> = [
  ['original', 'Original formatting'],
  ['reader', 'Reader text'],
  ['plain', 'Plain text'],
];

/** Mail providers whose IMAP settings the editor can fill in (sources.md "presets"). */
export const MAIL_PRESETS: ReadonlyArray<{ id: string; name: string; host: string; port: number; security: 'tls' | 'starttls'; note?: string }> = [
  { id: 'gmail', name: 'Gmail', host: 'imap.gmail.com', port: 993, security: 'tls', note: 'Gmail needs an app password (Google Account → Security → App passwords).' },
  { id: 'outlook', name: 'Outlook / Microsoft 365', host: 'outlook.office365.com', port: 993, security: 'tls' },
  { id: 'icloud', name: 'iCloud Mail', host: 'imap.mail.me.com', port: 993, security: 'tls', note: 'iCloud needs an app-specific password.' },
  { id: 'fastmail', name: 'Fastmail', host: 'imap.fastmail.com', port: 993, security: 'tls', note: 'Fastmail needs an app password.' },
  { id: 'yahoo', name: 'Yahoo Mail', host: 'imap.mail.yahoo.com', port: 993, security: 'tls', note: 'Yahoo needs an app password.' },
];

export const WATCH_PLATFORMS: ReadonlyArray<readonly [string, string]> = [
  ['webhook', 'Any webhook'],
  ['facebook', 'Facebook'],
  ['linkedin', 'LinkedIn'],
  ['x', 'X'],
  ['other', 'Other'],
];
